import atenia from "@/assets/project-atenia.webp";
import tot from "@/assets/project-tot-construccions.webp";
import seraudit from "@/assets/project-seraudit.webp";
import acamas from "@/assets/project-acamas.webp";
import chabaud from "@/assets/project-chabaud.webp";
import asselin from "@/assets/project-philippe-asselin.webp";
import gravel from "@/assets/project-ia-groupe.webp";
import avatarGravel from "@/assets/avatar-gravel.webp";
import avatarAcamas from "@/assets/avatar-acamas.webp";
import avatarPhilippe from "@/assets/avatar-philippe.webp";
import avatarProdrive from "@/assets/avatar-prodrive.webp";
import googleReviews from "@/components/home/google-reviews.json";

// Pages réalisations : uniquement des faits vérifiés dans les fiches projet.
export type CaseStudy = {
  slug: string;
  client: string;
  sector: string;
  place: string;
  url?: string;
  image: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  facts: { label: string; value: string }[];
  challenge: string[];
  work: { title: string; body: string }[];
  result: string[];
  // Témoignage déjà publié sur l'accueil (message original ou avis Google).
  quote?: { text: string; name: string; role: string; avatar: string; lang?: string };
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "atenia",
    client: "Atēnia",
    sector: "Sécurité alimentaire & environnement",
    place: "Andorre",
    url: "https://atenia.ad/",
    image: atenia,
    title: "Atēnia : un site de 60 pages en 4 langues, repris de zéro",
    metaTitle: "Réalisation : site web multilingue d'Atēnia (Andorre) | HTMC",
    metaDescription:
      "Migration du site d'Atēnia, organisme andorran de contrôle environnemental et de sécurité alimentaire : 60 pages, 4 langues, inscriptions aux formations en ligne.",
    intro:
      "Atēnia est un organisme andorran de contrôle environnemental et de sécurité alimentaire, accrédité par le ministère de l'Environnement. Son site devait présenter ses services, son blog et son catalogue de formations, en quatre langues.",
    facts: [
      { label: "Pages", value: "60 pages + 20 articles" },
      { label: "Langues", value: "Catalan, espagnol, français, anglais" },
      { label: "Outil", value: "WordPress + Bricks, autonome pour l'équipe" },
      { label: "SEO Lighthouse", value: "85 → 100" },
    ],
    challenge: [
      "L'ancien site tournait sur un CMS que l'équipe ne pouvait pas modifier seule. Chaque changement de texte ou de formation passait par un développeur.",
      "Les formations étaient saisies deux fois : sur le site principal et sur la plateforme d'inscription.",
    ],
    work: [
      {
        title: "Migration complète",
        body: "Les 60 pages et les 20 articles ont été repris dans WordPress, avec la charte d'Atēnia et une page par service et par formation.",
      },
      {
        title: "Quatre langues liées",
        body: "Chaque page existe en catalan, espagnol, français et anglais, reliée à ses traductions pour que Google affiche la bonne langue au bon visiteur.",
      },
      {
        title: "Formations synchronisées",
        body: "Les prochaines sessions sont lues automatiquement depuis l'agenda Google de l'équipe : une seule saisie, deux sites à jour.",
      },
      {
        title: "Inscription et paiement en ligne",
        body: "Le visiteur choisit sa formation, demande une facture s'il le souhaite et paie par carte, sans échange de mails.",
      },
    ],
    result: [
      "Une équipe autonome sur ses contenus.",
      "Après les corrections d'octobre 2026, le site obtient 100 en SEO et 92 en bonnes pratiques dans Lighthouse (contre 85 et 69 avant).",
    ],
  },
  {
    slug: "tot-construccions",
    client: "Tot Construccions",
    sector: "Construction & rénovation",
    place: "Escaldes-Engordany, Andorre",
    url: "https://www.totconstruccions.net/",
    image: tot,
    title: "Tot Construccions : un site pensé pour être trouvé en Andorre",
    metaTitle: "Réalisation : site web de Tot Construccions (Andorre) | HTMC",
    metaDescription:
      "Site vitrine en 4 langues pour Tot Construccions, entreprise de construction et de rénovation en Andorre depuis 2008 : devis, WhatsApp et SEO local.",
    intro:
      "Tot Construccions construit et rénove en Andorre depuis 2008. L'entreprise voulait un site qui montre ses chantiers et transforme les visites en demandes de devis, dans les langues de ses clients.",
    facts: [
      { label: "Langues", value: "Catalan, espagnol, français, anglais" },
      { label: "Contact", value: "Devis en ligne + WhatsApp" },
      { label: "SEO local", value: "Fiche entreprise structurée pour Google" },
      { label: "En ligne", value: "Octobre 2026" },
    ],
    challenge: [
      "Une entreprise reconnue localement, mais peu visible quand un particulier ou un promoteur cherche un constructeur sur Google.",
      "Une clientèle qui parle catalan, espagnol, français ou anglais selon les chantiers.",
    ],
    work: [
      {
        title: "Un site en quatre langues",
        body: "Sept pages traduites et liées entre elles, pour que chaque visiteur arrive directement dans sa langue.",
      },
      {
        title: "Le devis en un geste",
        body: "Formulaire de devis et bouton WhatsApp sur chaque page : le visiteur contacte l'entreprise sans chercher.",
      },
      {
        title: "SEO local dès le départ",
        body: "Titres et descriptions rédigés pour chaque page et chaque langue, adresse, téléphone et horaires déclarés à Google comme entreprise de construction à Escaldes-Engordany.",
      },
      {
        title: "Mise en ligne propre",
        body: "Nouveau domaine, HTTPS, redirections de l'adresse provisoire et plan du site contrôlé dans les quatre langues.",
      },
    ],
    result: [
      "Un site prêt à remonter sur les recherches locales, avec la fiche Google de l'entreprise comme prochaine étape.",
    ],
  },
  {
    slug: "prodrive",
    client: "Prodrive Andorre",
    sector: "Mécanique automobile",
    place: "Santa Coloma, Andorre",
    url: "https://www.prodrive.ad/",
    image: "/project-prodrive.webp",
    title: "Prodrive : un site premium pour un atelier mécanique",
    metaTitle: "Réalisation : site web de Prodrive, atelier mécanique en Andorre | HTMC",
    metaDescription:
      "Site vitrine pour Prodrive, atelier mécanique à Santa Coloma (Andorre) spécialisé dans les véhicules premium.",
    intro:
      "Prodrive est un atelier mécanique de Santa Coloma, spécialiste des véhicules premium. Le site devait refléter ce niveau d'exigence dès la première seconde.",
    facts: [
      { label: "Secteur", value: "Atelier mécanique, véhicules premium" },
      { label: "Lieu", value: "Santa Coloma, Andorre" },
      { label: "Type", value: "Site vitrine sur mesure" },
    ],
    challenge: [
      "Sur un marché de 85 000 habitants, tout se joue sur la réputation : le site doit rassurer un propriétaire de voiture haut de gamme avant même le premier appel.",
    ],
    work: [
      {
        title: "Une image premium",
        body: "Typographies et mise en page pensées pour les véhicules de luxe et haut de gamme confiés à l'atelier.",
      },
      {
        title: "Des services clairs",
        body: "Diagnostic, réparation et entretien présentés simplement, en catalan, avec téléphone et horaires visibles.",
      },
      {
        title: "Trouvable sur Google",
        body: "Titre, description et fiche « garage automobile » déclarés à Google avec l'adresse de Santa Coloma, pour les recherches du type « taller mecànic Andorra ».",
      },
    ],
    result: ["Un site qui positionne l'atelier sur le haut de gamme en Andorre."],
    quote: {
      text: googleReviews.reviews[0].excerpt,
      name: "ProDrive SL",
      role: "Avis Google",
      avatar: avatarProdrive,
      lang: googleReviews.reviews[0].language,
    },
  },
  {
    slug: "seraudit",
    client: "Seraudit",
    sector: "Expertise comptable & audit",
    place: "Andorre",
    image: seraudit,
    title: "Seraudit : un cabinet comptable chaleureux, pas corporate",
    metaTitle: "Réalisation : site web de Seraudit, cabinet comptable en Andorre | HTMC",
    metaDescription:
      "Site vitrine trilingue (catalan, français, anglais) pour Seraudit, cabinet d'expertise comptable et d'audit en Andorre.",
    intro:
      "Seraudit est un cabinet pionnier du conseil et de la comptabilité en Andorre. Il s'adresse aux entreprises locales comme aux investisseurs étrangers.",
    facts: [
      { label: "Langues", value: "Catalan, français, anglais" },
      { label: "Type", value: "Site vitrine léger, code sur mesure" },
      { label: "Conformité", value: "RGPD + loi andorrane (APDA)" },
    ],
    challenge: [
      "Le secteur est rempli de sites froids et interchangeables. Seraudit voulait un ton professionnel mais humain, et un ancrage andorran visible.",
    ],
    work: [
      {
        title: "Trois langues, trois publics",
        body: "Le catalan pour les clients andorrans, le français pour la clientèle frontalière, l'anglais pour les investisseurs.",
      },
      {
        title: "Un site rapide",
        body: "Code HTML et CSS sur mesure, sans surcharge, pour un affichage immédiat sur mobile.",
      },
      {
        title: "Un premier contact simple",
        body: "Une consultation gratuite mise en avant et un formulaire qui arrive directement dans la boîte du cabinet.",
      },
    ],
    result: ["Un site qui ressemble au cabinet : rigoureux, accessible et clairement andorran."],
  },
  {
    slug: "acamas",
    client: "Acamas Immobilier",
    sector: "Gestion immobilière",
    place: "Shawinigan & Montréal, Québec",
    url: "https://acamas.ca/",
    image: acamas,
    title: "Acamas : un site qui inspire confiance aux propriétaires",
    metaTitle: "Réalisation : site web d'Acamas Immobilier (Québec) | HTMC",
    metaDescription:
      "Site WordPress + Bricks pour Acamas Immobilier, gestionnaire immobilier au Québec : services présentés clairement et demandes de contact triées dès le formulaire.",
    intro:
      "Acamas acquiert, gère et valorise des immeubles résidentiels et commerciaux au Québec. Son site devait convaincre des propriétaires et des investisseurs de lui confier leurs biens.",
    facts: [
      { label: "Marché", value: "Québec, résidentiel et commercial" },
      { label: "Outil", value: "WordPress + Bricks" },
      { label: "Pages", value: "Accueil, services, à propos, équipe, contact" },
      { label: "Contact", value: "Formulaire trié par type de demande" },
    ],
    challenge: [
      "Confier un immeuble à un gestionnaire, c'est une décision de confiance. Le site devait montrer une entreprise sérieuse, disponible et transparente dès la page d'accueil.",
      "Les demandes arrivent pour des sujets très différents : gestion, location, augmentation de loyer. Il fallait les recevoir déjà classées.",
    ],
    work: [
      {
        title: "Un positionnement clair",
        body: "Une promesse simple en haut de page : la gestion immobilière clé en main pour investisseurs exigeants, en résidentiel comme en commercial.",
      },
      {
        title: "Quatre services lisibles",
        body: "Gestion immobilière, litiges, locations et entretien, chacun résumé en une ligne avec ce qu'il couvre concrètement.",
      },
      {
        title: "Des demandes déjà triées",
        body: "Le formulaire demande l'objet du message (gestion, location, augmentation, autre) : l'équipe sait qui rappeler et pour quoi.",
      },
      {
        title: "Une équipe visible",
        body: "Pages à propos et équipe, coordonnées, horaires et bureaux de Shawinigan et Montréal affichés partout.",
      },
    ],
    result: [
      "Un site moderne que l'équipe peut faire évoluer seule dans WordPress.",
      "Les premières demandes en ligne sont arrivées peu après la mise en ligne, selon les clients eux-mêmes.",
    ],
    quote: {
      text: "Exactement ce qu'on cherchait, moderne et professionnel. On a déjà reçu nos premières demandes en ligne.",
      name: "Noémie & Raoul",
      role: "Acamas Immobilier",
      avatar: avatarAcamas,
    },
  },
  {
    slug: "clinique-veterinaire-chabaud",
    client: "Clinique vétérinaire Chabaud",
    sector: "Clinique vétérinaire",
    place: "Carpentras, France",
    image: chabaud,
    title: "Clinique Chabaud : la refonte d'un site riche, sans perdre son référencement",
    metaTitle: "Réalisation : refonte du site de la clinique vétérinaire Chabaud | HTMC",
    metaDescription:
      "Refonte WordPress + Bricks du site de la clinique vétérinaire des docteurs Chabaud à Carpentras : services, conseils filtrés par animal et redirections SEO.",
    intro:
      "La clinique des docteurs Chabaud est une clinique vétérinaire à taille humaine à Carpentras. Son site, riche en services et en conseils, devait être modernisé sans perdre ce qu'il avait construit sur Google.",
    facts: [
      { label: "Type", value: "Refonte complète" },
      { label: "Outil", value: "WordPress + Bricks" },
      { label: "Contenu", value: "Services + conseils par animal" },
      { label: "SEO", value: "Redirections de toutes les anciennes pages" },
    ],
    challenge: [
      "L'ancien site contenait beaucoup d'informations utiles, mais il était daté et difficile à parcourir sur mobile.",
      "La clinique ne voulait pas repartir de zéro sur Google : les pages existantes devaient garder leur référencement.",
    ],
    work: [
      {
        title: "Maquettes validées avec la clinique",
        body: "Deux versions de la page d'accueil, construites avec les couleurs et les photos de la clinique, puis ajustées à partir de ses retours.",
      },
      {
        title: "Services et conseils en modèles",
        body: "Chaque service et chaque conseil suit le même modèle de page : la clinique ajoute un contenu sans toucher à la mise en page.",
      },
      {
        title: "Conseils filtrés par animal",
        body: "Les articles se filtrent en un clic : chat, chien ou NAC. Le propriétaire trouve directement ce qui concerne son animal.",
      },
      {
        title: "Les infos pratiques au premier plan",
        body: "Bouton de rendez-vous, urgences de nuit et de week-end, boutique en ligne et bandeau pour les horaires spéciaux ou les recrutements.",
      },
      {
        title: "Un site léger",
        body: "Plus de 130 images converties au format WebP, soit environ deux fois moins de poids à charger.",
      },
      {
        title: "Référencement préservé",
        body: "Un plan de redirection associe chaque ancienne adresse à sa nouvelle page, pour garder les positions acquises sur Google.",
      },
    ],
    result: ["Un site clair et rapide, que l'équipe de la clinique peut tenir à jour elle-même."],
  },
  {
    slug: "philippe-asselin",
    client: "Philippe Asselin",
    sector: "Courtier hypothécaire",
    place: "Québec, Canada",
    url: "https://passelinhypotheque.com/",
    image: asselin,
    title: "Philippe Asselin : un courtier hypothécaire trouvable à Québec",
    metaTitle: "Réalisation : site web de Philippe Asselin, courtier hypothécaire | HTMC",
    metaDescription:
      "Site WordPress de Philippe Asselin, courtier hypothécaire à Québec : services détaillés, fiche à télécharger, SEO local et maintenance par HTMC.",
    intro:
      "Philippe Asselin accompagne ses clients dans l'achat, le refinancement et le renouvellement de leur hypothèque à Québec. Son site doit rassurer avant un des plus gros engagements financiers d'une vie.",
    facts: [
      { label: "Services", value: "Préautorisation, refinancement, renouvellement" },
      { label: "Outil", value: "WordPress" },
      { label: "SEO local", value: "Fiche entreprise structurée pour Google" },
      { label: "Suivi", value: "Maintenance et mises à jour par HTMC" },
    ],
    challenge: [
      "Une hypothèque, c'est un sujet stressant. Le visiteur doit comprendre ce que fait un courtier et avoir envie d'appeler.",
      "Le site devait aussi être bien compris par Google : nom, adresse, téléphone et horaires du courtier, pour les recherches locales.",
    ],
    work: [
      {
        title: "Le rôle du courtier expliqué",
        body: "Huit étapes, d'écouter à suivre, pour montrer concrètement ce que Philippe fait pour son client.",
      },
      {
        title: "Une page par besoin",
        body: "Préautorisation, refinancement et renouvellement ont chacun leur page, avec une FAQ pour les questions fréquentes.",
      },
      {
        title: "SEO local remis d'aplomb",
        body: "Nom, adresse, téléphone et horaires déclarés à Google, données de profil corrigées et pages correctement typées.",
      },
      {
        title: "Un site entretenu",
        body: "WordPress et ses extensions à jour, sauvegardes, compte éditeur pour Philippe et coordonnées mises à jour quand il a changé de cabinet.",
      },
    ],
    result: [
      "Un site à jour et sécurisé, que Philippe peut modifier lui-même.",
      "Des informations locales propres, lues correctement par Google.",
    ],
    quote: {
      text: "Je suis très satisfait du travail accompli. Tu peux compter sur moi pour te référer et pour mes projets futurs. Sérieux, efficace et à l'écoute.",
      name: "Philippe Asselin",
      role: "Courtier hypothécaire",
      avatar: avatarPhilippe,
    },
  },
  {
    slug: "gravel-gestion-de-patrimoine",
    client: "Gravel Gestion de patrimoine",
    sector: "Conseil financier · iA Groupe financier",
    place: "Lévis, Québec",
    url: "https://gravelgp.com/",
    image: gravel,
    title: "Gravel Gestion de patrimoine : un conseiller qui prend ses rendez-vous en ligne",
    metaTitle: "Réalisation : site web de Gravel Gestion de patrimoine (Québec) | HTMC",
    metaDescription:
      "Site WordPress pour Michael Gravel, conseiller en gestion de patrimoine affilié à iA Groupe financier à Lévis : services expliqués simplement et prise de rendez-vous.",
    intro:
      "Michael Gravel est conseiller en gestion de patrimoine à Lévis, affilié à iA Groupe financier. Il voulait un site à son nom, qui explique son métier simplement et donne envie de prendre rendez-vous.",
    facts: [
      { label: "Services", value: "Épargne, retraite et assurances" },
      { label: "Outil", value: "WordPress" },
      { label: "Contact", value: "Prise de rendez-vous en un clic" },
      { label: "En ligne", value: "Janvier 2025" },
    ],
    challenge: [
      "Beaucoup de gens se demandent à quoi sert un conseiller financier. Le site devait répondre à cette question avant même le premier appel.",
      "Michael voulait une présence à son nom, distincte de celle de son réseau, avec ses propres services et ses coordonnées.",
    ],
    work: [
      {
        title: "Le métier expliqué simplement",
        body: "Une page d'accueil qui répond aux vraies questions : pourquoi un conseiller, quelles stratégies, quels gains de sérénité et de temps.",
      },
      {
        title: "Une page par service",
        body: "Épargne et retraite d'un côté, assurances de l'autre, chacun avec ce qu'il protège concrètement.",
      },
      {
        title: "Le rendez-vous en avant",
        body: "Bouton « Prendre rendez-vous » dès le haut de page, coordonnées et adresse de Lévis sur toutes les pages.",
      },
      {
        title: "Un client autonome",
        body: "Site livré avec ses accès et un guide pas à pas pour se connecter à WordPress et modifier ses contenus.",
      },
    ],
    result: ["Un site professionnel à son nom, qui amène de nouveaux prospects selon Michael lui-même."],
    quote: {
      text: "Un site professionnel qui reflète parfaitement notre image et qui nous génère de nouveaux prospects chaque semaine. L'accompagnement a été impeccable du début à la fin.",
      name: "Michael Gravel",
      role: "Conseiller en gestion de patrimoine",
      avatar: avatarGravel,
    },
  },
];

export const getCaseStudy = (slug?: string) => caseStudies.find((c) => c.slug === slug);
